import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Building2, CreditCard, User, Copy, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Bagis() {
  const [donationInfo, setDonationInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedField, setCopiedField] = useState(null);

  useEffect(() => {
    fetchDonationInfo();
  }, []);

  const fetchDonationInfo = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/donation-info`);
      if (response.ok) {
        const data = await response.json();
        setDonationInfo(data);
      }
    } catch (error) {
      console.error('Donation info fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success('Kopyalandı!');
    setTimeout(() => setCopiedField(null), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white flex items-center justify-center">
        <div className="text-xl text-amber-800">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 fade-in">
          <h1 className="text-4xl sm:text-5xl font-bold text-amber-900 mb-4" style={{ fontFamily: 'Merriweather, serif' }}>
            Bağış Yap
          </h1>
          <p className="text-lg text-amber-700">
            Desteğinizle daha fazla insana ulaşıyoruz
          </p>
        </div>

        <Card className="border-2 border-amber-200 bg-white shadow-xl mb-8">
          <CardHeader className="bg-gradient-to-r from-amber-100 to-orange-100">
            <CardTitle className="text-2xl text-amber-900" style={{ fontFamily: 'Merriweather, serif' }}>
              Bağış Bilgileri
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-8">
            {!donationInfo ? (
              <div className="text-center py-8">
                <p className="text-lg text-amber-700">Bağış bilgileri henüz eklenmemiş</p>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="bg-amber-50 rounded-lg p-6 border border-amber-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-amber-600 to-orange-600 rounded-full flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-amber-700 font-semibold">Banka Adı</p>
                      <p className="text-lg text-amber-900 font-bold" data-testid="bank-name">{donationInfo.bank_name}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-amber-50 rounded-lg p-6 border border-amber-200">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="w-10 h-10 bg-gradient-to-br from-amber-600 to-orange-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <CreditCard className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-amber-700 font-semibold mb-2">IBAN</p>
                        <p className="text-base md:text-lg text-amber-900 font-mono break-all" data-testid="iban-number">
                          {donationInfo.iban}
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={() => copyToClipboard(donationInfo.iban, 'iban')}
                      variant="outline"
                      size="sm"
                      className="border-amber-300 text-amber-900 hover:bg-amber-100 flex-shrink-0"
                      data-testid="copy-iban-btn"
                    >
                      {copiedField === 'iban' ? (
                        <CheckCircle className="w-4 h-4" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <div className="bg-amber-50 rounded-lg p-6 border border-amber-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-amber-600 to-orange-600 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-amber-700 font-semibold">Hesap Sahibi</p>
                      <p className="text-lg text-amber-900 font-bold" data-testid="account-holder">{donationInfo.account_holder}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-2 border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50">
          <CardContent className="p-8">
            <h3 className="text-2xl font-bold text-amber-900 mb-4" style={{ fontFamily: 'Merriweather, serif' }}>
              Bağışlarınız Nerede Kullanılıyor?
            </h3>
            <ul className="space-y-3 text-amber-800">
              <li className="flex items-start gap-3">
                <span className="text-orange-600 font-bold text-xl">•</span>
                <span>Eğitim yardımları ve burs destekleri</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-orange-600 font-bold text-xl">•</span>
                <span>İhtiyaç sahibi ailelere gıda ve giyim yardımları</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-orange-600 font-bold text-xl">•</span>
                <span>Sağlık hizmetleri ve tedavi destekleri</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-orange-600 font-bold text-xl">•</span>
                <span>Sosyal ve kültürel etkinlikler</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-orange-600 font-bold text-xl">•</span>
                <span>Altyapı geliştirme projeleri</span>
              </li>
            </ul>
            <p className="mt-6 text-amber-900 font-semibold text-center bg-white bg-opacity-50 p-4 rounded-lg">
              Her katkınız, bir umudun filizlenmesine vesile oluyor. Teşekkür ederiz!
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
